import { API, deleteRequest, getRequest, patchRequest, postRequest, putRequest } from '@/api/api';
import { isNotFound } from '@/common/error';
import { generateSearchParams } from '@/utils/generate-search-params';
import type { List } from '@/utils/list';

import {
  mapEventInviteAnswer,
  mapEventInviteAnswerState,
  mapEventInviteStatus,
  mapInviteCode,
  mapInviteToDto,
  mapJoinedVessel,
  mapSeatAvailability,
} from './invites.mappers';
import type {
  CreateInvitation,
  EventInviteAnswerChoice,
  EventInviteAnswerDto,
  EventInviteAnswerStateDto,
  EventInviteStatusDto,
  GenerateInviteLinkRequest,
  InviteCodeDto,
  InviteCodeRole,
  InviteLink,
  JoinInviteCodeDto,
  SeatAvailabilityDto,
  SingleInvite,
} from './invites.types';

export const invite = async (invite: CreateInvitation) => {
  await postRequest(API.PEOPLE, '/invites', mapInviteToDto(invite));
};

export const generateInviteLink = async (body: GenerateInviteLinkRequest) => {
  const { data } = await postRequest<InviteLink, GenerateInviteLinkRequest>(
    API.PEOPLE,
    '/invites/link',
    body,
  );
  return data;
};

/**
 * Mints a one-time code for `role` on the vessel. Scoped by an explicit `X-Service-Id`: the vessel
 * page can show a vessel other than the active account.
 */
export const createInviteCode = async (vesselId: string, role: InviteCodeRole) => {
  const { data } = await postRequest<InviteCodeDto, { role: InviteCodeRole }>(
    API.PEOPLE,
    '/invites/code',
    { role },
    false,
    vesselId,
  );
  return mapInviteCode(data);
};

/**
 * The vessel's pending codes, newest first, for its owner group or captain — on any plan, unlike
 * `GET /invites/vessel`, which needs crew management.
 */
export const getInviteCodes = async (vesselId: string) => {
  const { data } = await getRequest<InviteCodeDto[]>(
    API.PEOPLE,
    '/invites/code',
    false,
    undefined,
    vesselId,
  );
  return (data ?? []).map(mapInviteCode);
};

/** Puts the caller on the vessel the code was minted for, with its role, and spends the code. */
export const joinByInviteCode = async (code: string) => {
  const { data } = await postRequest<JoinInviteCodeDto, { code: string }>(
    API.PEOPLE,
    '/invites/code/join',
    { code },
  );
  return mapJoinedVessel(data);
};

export const getInviteById = async (id: string) => {
  try {
    const { data } = await getRequest<SingleInvite>(API.PEOPLE, `invites/${id}`);
    return data;
  } catch {
    return null;
  }
};

export const acceptInvitation = async (id: string) => {
  await putRequest(API.PEOPLE, `invites/${id}`, {});
};

export const joinByInviteLink = async (previewId: string) => {
  await postRequest(API.PEOPLE, `/invites/link/${encodeURIComponent(previewId)}/join`, {});
};

export const getInvites = async (params: {
  page: number;
  limit: number;
  person_id?: string;
  service_id?: string;
  event_id?: string;
}) => {
  const searchParams = generateSearchParams({ ...params, service_type: 'vessel' });
  const { data } = await getRequest<List<SingleInvite>>(API.PEOPLE, `invites?${searchParams}`);
  return data;
};

/** The invitation host's avatar; public, the preview id is the credential. */
export const eventInviteHostAvatarUrl = (previewId: string) =>
  `${import.meta.env.VITE_APP_PEOPLE_URL}/invites/preview/${encodeURIComponent(previewId)}/host-avatar`;

const answerPath = (previewId: string) => `invites/preview/${encodeURIComponent(previewId)}/answer`;

/**
 * The invitation's deadline, or why it lapsed while it is still kept; null once it is withdrawn or
 * cleaned up. Needs no account.
 */
export const getEventInviteStatus = async (previewId: string) => {
  try {
    const { data } = await getRequest<EventInviteStatusDto>(
      API.PEOPLE,
      `invites/preview/${encodeURIComponent(previewId)}/status`,
    );
    return mapEventInviteStatus(data);
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
};

/** What the contact holding a preview link answered; null until they answer. Needs no account. */
export const getEventInviteAnswer = async (previewId: string) => {
  try {
    const { data } = await getRequest<EventInviteAnswerStateDto>(API.PEOPLE, answerPath(previewId));
    return mapEventInviteAnswerState(data);
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
};

/** Accepts or declines an event invitation without an account. The first answer is final. */
export const answerEventInvite = async (
  previewId: string,
  answer: EventInviteAnswerChoice,
  reason?: string,
) => {
  const { data } = await postRequest<
    EventInviteAnswerStateDto,
    { answer: EventInviteAnswerChoice; reason?: string }
  >(API.PEOPLE, answerPath(previewId), {
    answer,
    reason: reason || undefined,
  });
  return mapEventInviteAnswerState(data);
};

/** What contacts without an account answered to the event's invitations. */
export const getEventInviteAnswers = async (serviceId: string, eventId: string) => {
  const searchParams = generateSearchParams({ service_id: serviceId, event_id: eventId });
  const { data } = await getRequest<EventInviteAnswerDto[]>(
    API.PEOPLE,
    `invites/answers?${searchParams}`,
  );
  return (data ?? []).map(mapEventInviteAnswer);
};

/** Changes how many guests a contact's answer covers, once its invitation is gone. */
export const updateEventInviteAnswerAdditionalGuests = async (
  id: string,
  additionalGuests: number,
) => {
  const { data } = await patchRequest<EventInviteAnswerDto, { additional_guests: number }>(
    API.PEOPLE,
    `invites/answers/${id}/additional-guests`,
    { additional_guests: additionalGuests },
  );
  return mapEventInviteAnswer(data);
};

/** Takes the contact off the event: their answer and the invitation it came from go. */
export const deleteEventInviteAnswer = async (id: string): Promise<void> => {
  await deleteRequest(API.PEOPLE, `invites/answers/${id}`);
};

/** Moves a pending event invitation's deadline; the invitee hears of it again. */
export const updateInviteDeadline = async (id: string, expiresAt: string) => {
  const { data } = await patchRequest<SingleInvite, { expires_at: string }>(
    API.PEOPLE,
    `invites/${id}/expiry`,
    { expires_at: expiresAt },
  );
  return data;
};

/** Changes how many guests a live event invitation covers, and its answer's count with it. */
export const updateInviteAdditionalGuests = async (id: string, additionalGuests: number) => {
  const { data } = await patchRequest<SingleInvite, { additional_guests: number }>(
    API.PEOPLE,
    `invites/${id}/additional-guests`,
    { additional_guests: additionalGuests },
  );
  return data;
};

export const deleteInvite = async (id: string, reason?: string): Promise<void> => {
  await deleteRequest(API.PEOPLE, `invites/${id}`, reason ? { reason } : undefined);
};

export const getSeatAvailability = async (serviceId: string, role: string) => {
  const searchParams = generateSearchParams({ service_id: serviceId, role });
  const { data } = await getRequest<SeatAvailabilityDto>(
    API.PEOPLE,
    `invites/seat-availability?${searchParams}`,
  );
  return mapSeatAvailability(data);
};
